<script setup lang="ts">
import { computed, ref } from "vue";
import {
    NModal,
    NCard,
    NSpace,
    NButton,
    NText,
    NIcon,
    NList,
    NListItem,
    NBlockquote,
} from "naive-ui";
import { $t } from "@/locales";
import TierProgressIndicator from "./tier-progress-indicator.vue";
import TierInfoCard from "./tier-info-card.vue";

interface Props {
    visible: boolean;
    currentTier: string;
    totalPaidInUnit: number;
    exchangeRate: number;
    isMobile: boolean;
    direction: "ltr" | "rtl";
    loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    loading: false,
});

const emit = defineEmits<{
    "update:visible": [value: boolean];
    upgrade: [requiredAmount: number];
    close: [];
}>();

const upgrading = ref(false);

const currentTierNumber = computed(() => {
    const lastChar = props.currentTier.slice(-1);
    return Number(lastChar) || 0;
});

const isMaxTier = computed(() => {
    return currentTierNumber.value >= 5;
});

const handleClose = () => {
    emit("update:visible", false);
    emit("close");
};

const handleUpgrade = async (requiredAmount: number) => {
    try {
        upgrading.value = true;
        emit("upgrade", requiredAmount);
    } catch (error) {
        console.error("Upgrade error:", error);
    } finally {
        upgrading.value = false;
    }
};

const getTierTitle = () => {
    if (isMaxTier.value) {
        return $t("limits.maxTierTitle");
    }
    return $t("limits.upgradeTitle");
};

const getTierDescription = () => {
    if (isMaxTier.value) {
        return $t("limits.maxTierDescription");
    }
    return $t("limits.upgradeDescription");
};

const steps = [
    {
        title: $t("limits.step1Title"),
        description: $t("limits.step1Description"),
    },
    {
        title: $t("limits.step2Title"),
        description: $t("limits.step2Description"),
    },
    {
        title: $t("limits.step3Title"),
        description: $t("limits.step3Description"),
    },
    {
        title: $t("limits.step4Title"),
        description: $t("limits.step4Description"),
    },
];

const notes = [
    { text: $t("limits.note1"), type: "success" },
    { text: $t("limits.note2"), type: "success" },
    { text: $t("limits.note3"), type: "info" },
];
</script>

<template>
    <NModal :show="visible" :mask-closable="!upgrading" :closable="!upgrading" :close-on-esc="!upgrading" preset="card"
        style="
      width: 95%;
      max-width: 50rem;
      height: 90%;
      min-height: 20rem;
      margin-top: 2rem;
      margin-bottom: 2rem;
    " size="small" @update:show="handleClose">
        <template #header>
            <div class="w-full flex items-center justify-center">
                <NGradientText type="info">
                    {{ getTierTitle() }}
                </NGradientText>
            </div>
        </template>

        <NSpace vertical :size="24" :class="[props.direction]">
            <NBlockquote type="success" strong>
                {{ getTierDescription() }}
            </NBlockquote>

            <!-- Tier Progress Visualization -->
            <div>
                <NText strong style="display: block; margin-bottom: 16px">{{
                    $t("limits.tierProgress")
                }}</NText>
                <TierProgressIndicator :current-tier="currentTier" :total-paid-in-unit="totalPaidInUnit"
                    :is-mobile="isMobile" :direction="direction" />
            </div>

            <!-- Tier Information and Upgrade -->
            <TierInfoCard :current-tier="currentTier" :total-paid-in-unit="totalPaidInUnit"
                :exchange-rate="exchangeRate" :is-mobile="isMobile" :direction="direction" @upgrade="handleUpgrade" />

            <!-- How It Works Section -->
            <NCard size="small">
                <template #header>
                    <NText strong>{{ $t("limits.howItWorks") }}</NText>
                </template>

                <NSpace vertical :size="16">
                    <div v-for="(step, index) in steps" :key="index"
                        style="display: flex; align-items: flex-start; gap: 12px">
                        <div style="
                width: 24px;
                height: 24px;
                border-radius: 50%;
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                color: white;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: 600;
                font-size: 12px;
                flex-shrink: 0;
              ">
                            {{ index + 1 }}
                        </div>
                        <div>
                            <NText strong style="display: block; margin-bottom: 4px">{{
                                step.title
                            }}</NText>
                            <NText depth="3">{{ step.description }}</NText>
                        </div>
                    </div>
                </NSpace>
            </NCard>

            <!-- Important Notes -->
            <NCard size="small">
                <template #header>
                    <NText strong>{{ $t("limits.importantNotes") }}</NText>
                </template>

                <NList>
                    <NListItem v-for="(note, index) in notes" :key="index">
                        <template #prefix>
                            <NIcon :color="note.type === 'success' ? '#52c41a' : '#1890ff'" size="24">
                                <svg v-if="note.type === 'success'" xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24">
                                    <path fill="currentColor"
                                        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                                </svg>
                                <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                    <path fill="currentColor"
                                        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                                </svg>
                            </NIcon>
                        </template>
                        <NText>{{ note.text }}</NText>
                    </NListItem>
                </NList>
            </NCard>
        </NSpace>

        <template #footer>
            <div style="display: flex; justify-content: center">
                <NButton @click="handleClose" :disabled="upgrading">
                    {{ $t("common.close") }}
                </NButton>
            </div>
        </template>
    </NModal>
</template>

<style scoped>
/* Minimal custom styles - let Naive UI handle theming */
</style>
